import {
    createStyles,
    List,
    ListItem,
    ListItemText,
    makeStyles,
} from "@material-ui/core";
import { format } from "date-fns";
import qs from "qs";
import * as React from "react";

import {
    Divider,
    ISearchFacetConfig,
    Link,
    MobilePage,
    MobilePageActions,
    MobilePageSection,
    PaginatorMobile,
    RelodeTheme,
    SearchFiltersMobile,
    SelectInput,
    Text,
    useRouter,
} from "../../../";
import { ChevronRightIcon, FilterIcon } from "../../../assets/icons";
import {
    Message,
    MessageBoxOptionsEnum,
    MessageSearchCriteria,
} from "../types";
import { useMessageSearch } from "./_infra/messageListingProvider";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            container: {
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
            },
            header: {
                display: "flex",
                justifyContent: "space-between",
                marginRight: theme.spacing(2),
            },
            subheader: {
                display: "flex",
                flexDirection: "row",
            },
            navigationIcon: {
                marginLeft: theme.spacing(1),
            },
            filtersLink: {
                position: "absolute",
                right: 0,
                marginRight: theme.spacing(2),
            },
            label: {
                color: theme.relode.colors.label.main,
            },
            noMessages: {
                display: "flex",
                alignItems: "center",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                position: "absolute",
            },
            noMessagesInfo: {
                padding: theme.spacing(3),
                textAlign: "center",
                width: "100%",
                alignSelf: "center",
            },
            listItem: {
                backgroundColor: "white",
            },
            listItemText: {
                marginLeft: theme.spacing(2),
            },
            truncateText: {
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                maxWidth: 0,
            },
            input: {
                margin: 0,
            },
        }),
    { name: "MessageListingMobile" }
);

export const MessageListingMobile = () => {
    const classes = useStyles({});
    const { history, location } = useRouter();
    const { state: listingState, actions: listingActions } = useMessageSearch();
    const { searchResults, searchCriteria } = listingState;

    const [isFiltersSectionOpen, setIsFiltersSectionOpen] =
        React.useState<boolean>(false);
    const [selectedBox, setSelectedBox] = React.useState<MessageBoxOptionsEnum>(
        MessageBoxOptionsEnum.Inbox
    );

    React.useEffect(() => {
        setSelectedBox(
            searchCriteria.box
                ? MessageBoxOptionsEnum.fromName(searchCriteria.box)
                : MessageBoxOptionsEnum.Inbox
        );
    }, [searchCriteria]);

    const goToDetails = (message: Message) => {
        const { messageIds } = searchResults;
        const queryString = qs.stringify(searchCriteria, {
            skipNulls: true,
            sort: (a, b) => a.localeCompare(b),
        });

        history.push({
            pathname: `/messages/${message.messageId}`,
            state: {
                ...location.state,
                messageIds,
                queryString,
            },
        });
    };
    const handleSearch = async (searchText?: string) => {
        const sortField =
            searchCriteria.searchText !== searchText
                ? "rank"
                : searchCriteria.sortField;
        const sortDir =
            searchCriteria.searchText !== searchText
                ? "desc"
                : searchCriteria.sortDir;

        const newCriteria: MessageSearchCriteria = {
            ...searchCriteria,
            box: selectedBox.name,
            searchText: searchText,
            sortField,
            sortDir,
        };

        await listingActions.setSearchCriteria(newCriteria);
        setIsFiltersSectionOpen(false);
    };

    const changePage = (direction: "prev" | "next") => {
        listingActions.navigate(direction);
        window.scrollTo(0, 0);
    };

    const renderPagination = (selectedSection: string): React.ReactNode => {
        const { totalResults } = searchResults;
        const { pageSize, pageIndex } = searchCriteria;

        const start = (pageIndex - 1) * pageSize + 1;
        const remaining =
            totalResults < pageSize ? totalResults : totalResults - start;
        const maxOnPage =
            remaining < pageSize ? totalResults : pageIndex * pageSize;

        if (!!listingState.searchResults && totalResults > 0) {
            return (
                <PaginatorMobile
                    baseId="searchMessages"
                    total={totalResults}
                    range={{
                        start,
                        end: maxOnPage,
                    }}
                    showAsRange
                    navigate={changePage}
                />
            );
        } else {
            return null;
        }
    };

    const handleFilter =
        (fId: string, fName: string) =>
        (catItem?: { label: string; value: string }) => {
            listingActions.filterMessages({
                id: fId,
                name: fName,
                item: catItem,
            });
        };

    const handleBoxFilter = (event: never, value: MessageBoxOptionsEnum) => {
        setSelectedBox(value);
    };

    const facetConfig: ISearchFacetConfig = {
        handleSingleSelection: handleFilter,
        facets: [
            {
                id: "status",
                title: "message status",
                idPropName: "messageStatusId",
                namePropName: "messageStatusName",
                isMultiple: false,
            },
            {
                id: "fromRole",
                title: "from",
                idPropName: "fromRoleId",
                namePropName: "fromRoleName",
                isMultiple: false,
            },
            {
                id: "toRole",
                title: "to",
                idPropName: "toRoleId",
                namePropName: "toRoleName",
                isMultiple: false,
            },
            // These are "fake" facets; they are used only the display the query params values to refined by section;
            // if those filters are removed than these facets will not be displayed since they are not returned by API endpoint
            {
                id: "contextJob",
                title: "contextJob",
                idPropName: "contextJobId",
                namePropName: "contextJobName",
                isMultiple: false,
            },
            {
                id: "contextUser",
                title: "contextUser",
                idPropName: "contextUserId",
                namePropName: "contextUserName",
                isMultiple: false,
            },
        ],
    };

    const customFilters = (
        <SelectInput
            mode="list"
            options={MessageBoxOptionsEnum.asArray().filter(
                el =>
                    !(
                        appConfig.appName !== "admin" &&
                        el.name === MessageBoxOptionsEnum.AllMessages.name
                    )
            )}
            getOptionLabel={option => option.label}
            label="mail folder"
            onChange={handleBoxFilter}
            value={selectedBox}
            disableTyping
        />
    );

    return (
        <>
            <MobilePage
                pageTitle="messages"
                defaultSection="search"
                showTabs={false}
                busyType="bar"
                isBusy={listingState.loadingMessages}
                actions={
                    <MobilePageActions title="messages">
                        <Link
                            className={classes.filtersLink}
                            highlightType="internal"
                            onClick={() => setIsFiltersSectionOpen(true)}
                        >
                            <Text variant="body1" color="primary">
                                <FilterIcon fontSize="small" /> filters
                            </Text>
                        </Link>
                    </MobilePageActions>
                }
                footerActions={renderPagination}
            >
                <MobilePageSection id="search">
                    <div className={classes.container}>
                        {searchResults.messages &&
                        searchResults.messages.length > 0 ? (
                            <List id="messageListing">
                                {searchResults.messages.map((message, i) => (
                                    <MessageListItem
                                        message={message}
                                        onClick={goToDetails}
                                        key={i}
                                    />
                                ))}
                            </List>
                        ) : !listingState.loadingMessages ? (
                            <div className={classes.noMessagesInfo}>
                                <Text variant="h4" color="primary">
                                    Sorry, no messages found.
                                </Text>
                            </div>
                        ) : null}
                    </div>
                </MobilePageSection>
            </MobilePage>
            <SearchFiltersMobile
                isOpen={isFiltersSectionOpen}
                onOpenChanged={(val: boolean) => setIsFiltersSectionOpen(val)}
                searchCriteria={listingState.searchCriteria}
                onSearch={handleSearch}
                facets={searchResults?.facets}
                facetConfig={facetConfig}
                hasResults={searchResults?.totalResults > 0}
                customFilters={customFilters}
            />
        </>
    );
};

const MessageListItem = (props: {
    message: Message;
    onClick: (message: Message) => void;
}) => {
    const { message, onClick } = props;
    const classes = useStyles({});

    const listItemPrimaryText = (message: Message) => (
        <div className={classes.header}>
            {!!message.sender ? (
                <Text
                    variant="h5"
                    color={message.isRead ? "default" : "primary"}
                >
                    {message.sender.displayName}
                </Text>
            ) : (
                <Text variant="h5">{message.firstRecipient.displayName}</Text>
            )}

            <div className={classes.subheader}>
                <Text variant="body1" className={classes.label}>
                    {format(message.createTimestamp, "P")}
                </Text>
                <ChevronRightIcon
                    color="primary"
                    className={classes.navigationIcon}
                />
            </div>
        </div>
    );

    const handleClick = () => {
        onClick(message);
    };

    return (
        <>
            <ListItem
                button
                disableGutters
                alignItems="flex-start"
                onClick={handleClick}
                className={classes.listItem}
            >
                <ListItemText
                    primary={listItemPrimaryText(message)}
                    secondary={
                        <Text
                            component="span"
                            color={message.isRead ? "default" : "primary"}
                        >
                            {message.subject}
                        </Text>
                    }
                    className={classes.listItemText}
                />
            </ListItem>
            <Divider></Divider>
        </>
    );
};
